import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronRight } from "lucide-react";
import { TIMELINE } from "./data";

const TAG_COLORS = {
  School: "#a5b4fc",
  Sports: "#fbbf24",
  Achievement: "#e6c769",
  Research: "#c4b5fd",
  Work: "#86efac",
  Project: "#7dd3fc",
  Vision: "#f0abfc",
};

const Timeline = () => {
  const [active, setActive] = React.useState(0);

  return (
    <section id="timeline" className="relative py-28 px-6 md:px-10">
      <div className="max-w-[1300px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex items-end justify-between flex-wrap gap-6 mb-14"
        >
          <div>
            <div className="text-[11px] tracking-[0.4em] uppercase text-[#e6c769]/80 font-mono">03 / The Timeline</div>
            <h2
              className="mt-3 text-[44px] md:text-[80px] leading-[0.95] tracking-tight text-white"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              From <span className="italic">2017</span> to <span className="italic">now</span>.
            </h2>
          </div>
          <div className="max-w-md text-white/55 text-[15px] leading-relaxed">
            Eight years of school, sport, research, work, and quiet building. Click a marker to step inside the moment.
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-[1fr_2fr] gap-10">
          {/* Markers */}
          <div className="relative">
            <div className="absolute left-[14px] top-2 bottom-2 w-px bg-gradient-to-b from-transparent via-white/15 to-transparent" />
            <div className="space-y-3">
              {TIMELINE.map((m, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  className="w-full text-left flex items-start gap-5 group"
                >
                  <div
                    className={`relative mt-1 w-[30px] h-[30px] shrink-0 rounded-full grid place-items-center transition-all ${
                      active === i
                        ? "bg-[#e6c769] text-black"
                        : "bg-white/[0.04] border border-white/15 text-white/60 group-hover:border-white/40"
                    }`}
                  >
                    <span className="text-[9px] font-mono">{i + 1}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <span
                        className={`text-[12px] font-mono tracking-wider ${
                          active === i ? "text-[#e6c769]" : "text-white/40"
                        }`}
                      >
                        {m.year}
                      </span>
                      {m.tags?.map((t) => (
                        <span
                          key={t}
                          className="text-[9px] tracking-[0.2em] uppercase px-2 py-0.5 rounded-full border"
                          style={{ borderColor: `${TAG_COLORS[t] || "#aaa"}55`, color: TAG_COLORS[t] || "#aaa" }}
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                    <div
                      className={`mt-1 text-[15px] md:text-[17px] truncate transition-colors ${
                        active === i ? "text-white" : "text-white/70 group-hover:text-white"
                      }`}
                    >
                      {m.title}
                    </div>
                  </div>
                  <ChevronRight
                    className={`mt-1 w-4 h-4 shrink-0 transition-all ${
                      active === i ? "text-[#e6c769] translate-x-1" : "text-white/30"
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Detail */}
          <div className="sticky top-28 self-start">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="relative rounded-[24px] border border-white/10 bg-gradient-to-br from-white/[0.06] to-white/[0.02] backdrop-blur-xl overflow-hidden"
              >
                <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[#e6c769]/10 blur-3xl" />
                <div className="p-8 md:p-12 relative">
                  <div className="text-[140px] md:text-[180px] font-extrabold leading-none text-white/[0.06] absolute -top-6 right-6 select-none" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {TIMELINE[active].year}
                  </div>
                  <div className="relative">
                    <div className="text-[11px] tracking-[0.4em] uppercase text-[#e6c769] font-mono">{TIMELINE[active].year}</div>
                    <h3 className="mt-3 text-[32px] md:text-[44px] leading-tight text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {TIMELINE[active].title}
                    </h3>
                    {TIMELINE[active].place && (
                      <div className="mt-2 text-[14px] tracking-wide text-white/60">{TIMELINE[active].place}</div>
                    )}
                    <p className="mt-6 max-w-xl text-[16px] leading-[1.7] text-white/65">
                      {TIMELINE[active].text}
                    </p>
                    <div className="mt-8 flex flex-wrap gap-2">
                      {TIMELINE[active].tags?.map((t) => (
                        <span
                          key={t}
                          className="text-[11px] tracking-[0.2em] uppercase px-3 py-1 rounded-full border"
                          style={{ borderColor: `${TAG_COLORS[t] || "#aaa"}55`, color: TAG_COLORS[t] || "#aaa" }}
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Timeline;
